import type { Grade } from "@/ts/interfaces/student/studentAccountInterface";
import { getBaseRoute } from "@/utils/route";
import { mapGradeCodeToGradeParamId } from "@/utils/routeUtil";

/**
 * Build the orientation entry URL for a given grade.
 * Produces: `/learning/{gradeParam}/orientation`
 */
export const getOrientationEntryUrl = (gradeCode: number): string => {
  const baseRoute = getBaseRoute();
  const normalizedBaseRoute = baseRoute.endsWith("/")
    ? baseRoute.slice(0, -1)
    : baseRoute;
  const gradeParam = mapGradeCodeToGradeParamId(gradeCode) ?? "g6";
  return `${normalizedBaseRoute}/${gradeParam}/orientation`;
};

export const shouldRedirectToOrientation = ({
  orientationEnabled,
  grade
}: {
  orientationEnabled?: boolean;
  grade?: Grade | null;
}): boolean =>
  Boolean(orientationEnabled && grade && !grade.orientation_completed);

export const shouldShowPortalIntroSlide = (
  introScreenSeen?: boolean
): boolean => introScreenSeen !== true;

export const getOrientationStartUrl = (
  gradeCode: number,
  introScreenSeen?: boolean
): string => {
  if (shouldShowPortalIntroSlide(introScreenSeen)) {
    return getOrientationEntryUrl(gradeCode);
  }

  return getOrientationPath("/orientation/welcome", gradeCode);
};

/**
 * Build a grade-prefixed orientation step URL.
 *
 * `path` is a step-relative path such as `/orientation/subjects`.
 * The grade param (e.g. `g6`) is injected before the `/orientation` segment,
 * producing `/learning/g6/orientation/subjects`.
 */
export const getOrientationPath = (path: string, gradeCode: number): string => {
  const baseRoute = getBaseRoute();
  const normalizedBaseRoute = baseRoute.endsWith("/")
    ? baseRoute.slice(0, -1)
    : baseRoute;
  const gradeParam = mapGradeCodeToGradeParamId(gradeCode) ?? "g6";

  let resolvedPath: string;

  if (path.startsWith("http")) {
    resolvedPath = path;
  } else if (path.startsWith("/orientation")) {
    // Step-relative path from config: `/orientation/subjects` -> `/learning/g6/orientation/subjects`
    resolvedPath = `${normalizedBaseRoute}/${gradeParam}${path}`;
  } else if (path.startsWith(baseRoute)) {
    // Already fully-qualified (e.g. `/learning/g6/orientation/subjects`) — use as-is
    resolvedPath = path;
  } else if (path.startsWith("/")) {
    resolvedPath = `${normalizedBaseRoute}/${gradeParam}${path}`;
  } else {
    resolvedPath = `${normalizedBaseRoute}/${gradeParam}/${path}`;
  }

  return resolvedPath;
};
